// Order Status — Square-return landing page.
//
// The browser returning from Square is NOT proof of payment. This page
// reads the order_id appended by SQUARE_CHECKOUT_REDIRECT_URL and calls
// the trusted get_customer_order_status(p_order_id) RPC to render the
// authoritative order/payment state persisted by
// square-payment-webhook. If the webhook has not yet processed the
// event when the customer lands here, the page performs a bounded,
// polite poll (up to ~30 s) before surfacing a "processing" state so
// the customer can refresh later without an endless loop.

import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { BrandButton } from '../components/BrandButton';
import { useSession } from '../lib/useSession';
import { supabase, supabaseConfigured } from '../lib/supabase';
import { clearCheckoutRequestId } from '../lib/squareCheckout';

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 10; // ~30 seconds total

function logSanitized(label, err) {
  try {
    console.warn('[etff:order]', label, {
      name: err?.name || null,
      code: err?.code || null,
      message: err?.message || null,
    });
  } catch { /* ignore */ }
}

async function rpcGetCustomerOrderStatus(orderId) {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'Order status is temporarily unavailable.' };
  }
  try {
    const { data, error } = await supabase.rpc('get_customer_order_status', {
      p_order_id: orderId,
    });
    if (error) {
      logSanitized('rpc:error', error);
      return { ok: false, data: null, error: 'We could not load your order status.' };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('rpc:threw', err);
    return { ok: false, data: null, error: 'We could not load your order status.' };
  }
}

function readOrderId(search) {
  const q = new URLSearchParams(search || '');
  for (const k of ['order_id', 'orderId', 'order']) {
    const v = q.get(k);
    if (v && v.length) return v;
  }
  return null;
}

function readOutcome(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const o = payload.outcome || payload.status || payload.order_status;
  return typeof o === 'string' ? o : null;
}

function isTerminal(outcome) {
  return (
    outcome === 'confirmed' ||
    outcome === 'paid' ||
    outcome === 'cancelled' ||
    outcome === 'payment_failed' ||
    outcome === 'refunded'
  );
}

function isProcessing(outcome) {
  return (
    outcome === 'pending' ||
    outcome === 'processing' ||
    outcome === 'payment_processing' ||
    outcome === 'awaiting_payment'
  );
}

function displayFor(outcome) {
  switch (outcome) {
    case 'confirmed':
    case 'paid':
      return {
        eyebrow: 'Order confirmed',
        title: 'You&rsquo;re all set',
        tone: 'success',
        body: "Thanks! Your order is confirmed. We'll have your meals ready at your chosen pickup time.",
      };
    case 'cancelled':
      return {
        eyebrow: 'Checkout cancelled',
        title: 'Checkout cancelled',
        tone: 'warning',
        body: "It looks like the checkout was cancelled and no payment was taken. You can review your order and try again.",
      };
    case 'payment_failed':
      return {
        eyebrow: 'Payment not completed',
        title: 'Payment not completed',
        tone: 'danger',
        body: 'Your payment did not complete. Please review your order and try again.',
      };
    case 'refunded':
      return {
        eyebrow: 'Refunded',
        title: 'This order was refunded',
        tone: 'warning',
        body: 'This order has been refunded. If you need help, please contact us.',
      };
    case 'pending':
    case 'processing':
    case 'payment_processing':
    case 'awaiting_payment':
      return {
        eyebrow: 'Payment processing',
        title: 'Confirming your payment…',
        tone: 'info',
        body: "This usually takes a few seconds. We'll update this page automatically.",
      };
    default:
      return {
        eyebrow: 'Order status',
        title: 'Checking your order…',
        tone: 'info',
        body: 'One moment while we look up your latest status.',
      };
  }
}

export default function OrderStatus() {
  const { session, loading: sessionLoading, configured } = useSession();
  const nav = useNavigate();
  const location = useLocation();
  const orderId = readOrderId(location.search);

  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [polling, setPolling] = useState(true);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => { cancelledRef.current = true; };
  }, []);

  useEffect(() => {
    if (sessionLoading) return;
    if (!session) return;         // <Navigate/> below
    if (!orderId) return;
    let timer = null;

    async function tick(n) {
      if (cancelledRef.current) return;
      const r = await rpcGetCustomerOrderStatus(orderId);
      if (cancelledRef.current) return;
      if (!r.ok) {
        setError(r.error);
        setPolling(false);
        return;
      }
      setPayload(r.data);
      setError(null);
      const outcome = readOutcome(r.data);
      if (isTerminal(outcome)) {
        setPolling(false);
        // Order is settled — the previous checkout attempt is done;
        // future orders should mint a fresh UUID.
        clearCheckoutRequestId();
        return;
      }
      if (n >= POLL_MAX_ATTEMPTS - 1) {
        setPolling(false);
        return;
      }
      setAttempts(n + 1);
      timer = setTimeout(() => tick(n + 1), POLL_INTERVAL_MS);
    }

    tick(0);
    return () => { if (timer) clearTimeout(timer); };
  }, [session, sessionLoading, orderId]);

  if (!configured) {
    return (
      <AppShell>
        <div className="wizard-card">
          <div className="eyebrow">Order</div>
          <h1 className="h2 mt-3">Order status is temporarily unavailable</h1>
          <p className="mt-4" style={{ color: 'var(--text-dim)' }}>Please try again in a moment.</p>
        </div>
      </AppShell>
    );
  }
  if (sessionLoading) return null;
  if (!session) return <Navigate to="/sign-in" replace state={{ returnTo: `/plan/order${typeof location.search === 'string' ? location.search : ''}` }} />;

  if (!orderId) {
    return (
      <AppShell>
        <div className="wizard-card" data-testid="order-status-missing">
          <div className="eyebrow">Order</div>
          <h1 className="h2 mt-3">We couldn&rsquo;t find your order</h1>
          <p className="mt-4" style={{ color: 'var(--text-dim)' }}>
            The order reference is missing from this link. If you just completed
            checkout, please check your email confirmation or contact us.
          </p>
          <div className="mt-6">
            <BrandButton type="button" variant="primary" onClick={() => nav('/plan/results')}
              data-testid="order-status-back-btn">Back to my plan</BrandButton>
          </div>
        </div>
      </AppShell>
    );
  }

  const outcome = readOutcome(payload);
  const view = displayFor(outcome);
  const isTerminalOutcome = isTerminal(outcome);
  const showRefresh = !polling && !isTerminalOutcome && isProcessing(outcome);

  const toneColor =
    view.tone === 'success' ? 'var(--success, #2e7d32)' :
    view.tone === 'warning' ? 'var(--warning, #b26a00)' :
    view.tone === 'danger'  ? 'var(--danger, #b03a2e)' :
    'var(--ink-2)';

  return (
    <AppShell>
      <div className="wizard-card" data-testid="order-status">
        <div className="eyebrow" style={{ color: toneColor }}
          data-testid="order-status-eyebrow">{view.eyebrow}</div>
        <h1 className="h2 mt-3"
          data-testid="order-status-title"
          dangerouslySetInnerHTML={{ __html: view.title }} />
        <p className="mt-3" style={{ color: 'var(--text-dim)' }}
          data-testid="order-status-body">{view.body}</p>

        {polling && !isTerminalOutcome && (
          <div className="mt-4 flex items-center gap-3" data-testid="order-status-polling">
            <div style={{
              width: 20, height: 20,
              border: '3px solid var(--cream-2)',
              borderTopColor: 'var(--orange)',
              borderRadius: '50%',
              animation: 'spin 0.9s linear infinite',
            }} />
            <span className="text-sm" style={{ color: 'var(--text-dim)' }}>
              Checking with Square… (attempt {attempts + 1} of {POLL_MAX_ATTEMPTS})
            </span>
          </div>
        )}

        {error && (
          <div className="mt-4 p-3"
            style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)' }}
            data-testid="order-status-error">
            <p className="text-sm" style={{ color: 'var(--ink-2)' }}>{error}</p>
          </div>
        )}

        <div className="mt-6 flex gap-3 flex-wrap" data-testid="order-status-actions">
          {showRefresh && (
            <BrandButton type="button" variant="primary"
              onClick={() => { setPolling(true); setAttempts(0); }}
              data-testid="order-status-refresh-btn">Check again</BrandButton>
          )}
          {(outcome === 'cancelled' || outcome === 'payment_failed') && (
            <BrandButton type="button" variant="primary"
              onClick={() => nav('/plan/review')}
              data-testid="order-status-retry-btn">Back to review order</BrandButton>
          )}
          <BrandButton type="button" variant="outline"
            onClick={() => nav('/plan/results')}
            data-testid="order-status-plan-btn">Back to my plan</BrandButton>
        </div>

        <p className="mt-6 text-xs" style={{ color: 'var(--text-dim)' }}
          data-testid="order-status-order-id">
          Order reference: {orderId}
        </p>
      </div>
    </AppShell>
  );
}
